import * as Sentry from '@sentry/react'
import { ENV } from '@volee/config'

/** Sentry, and the three constraints the minors context imposes: see the README. */

/** One entry per concept: `fold` below makes the spelling irrelevant. */
const SENSITIVE_KEYS = [
  'email',
  'password',
  'token',
  'authorization',
  'codicefiscale',
  'taxcode',
  'phone',
  'telefono',
  'birthdate',
  'datanascita',
]

/** A querystring can carry identifiers: it is dropped, not filtered. */
function urlWithoutQuery(url: string): string {
  const cut = url.search(/[?#]/)
  return cut === -1 ? url : url.slice(0, cut)
}

/** The leva link's token opens a family's wizard, so it is a credential, live in the path
 *  and not the query string that `urlWithoutQuery` already drops. */
function redactInviteLinkToken(url: string): string {
  return url.replace(/(\/team-invite-links\/)[^/?#]+/, '$1[removed]')
}

function scrubUrl(url: string): string {
  return redactInviteLinkToken(urlWithoutQuery(url))
}

/** Letters and digits only, so one entry matches `tax_code`, `taxCode` and `tax-code`. */
function fold(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function isSensitive(key: string): boolean {
  const folded = fold(key)
  return SENSITIVE_KEYS.some((sensitive) => folded.includes(sensitive))
}

/** What a Sentry event holds, declared so `unknown` does not spread through `scrub`. */
export type EventValue =
  string | number | boolean | null | undefined | EventValue[] | { [key: string]: EventValue }

/** Strips sensitive values out of an event, deeply: it changes values, never keys. Exported
 * because it has to work whether or not anybody ever opens Sentry. */
export function scrub(value: EventValue): EventValue {
  if (Array.isArray(value)) return value.map(scrub)
  if (value === null || typeof value !== 'object') return value

  const scrubbed: { [key: string]: EventValue } = {}
  for (const [key, content] of Object.entries(value)) {
    if (isSensitive(key)) {
      scrubbed[key] = '[removed]'
    } else if (key === 'url' && typeof content === 'string') {
      scrubbed[key] = scrubUrl(content)
    } else {
      scrubbed[key] = scrub(content)
    }
  }
  return scrubbed
}

/** The DSN, or an empty string when it is not configured. */
function configuredDsn(): string {
  const dsn = import.meta.env?.VITE_SENTRY_DSN
  return typeof dsn === 'string' ? dsn.trim() : ''
}

/** Initialises Sentry when a DSN is present. The return value exists for the test: «with no
 * DSN it does not initialise» is an assertion, not a promise made in a comment. */
export function initSentry(): boolean {
  const dsn = configuredDsn()
  if (dsn === '') return false

  Sentry.init({
    dsn,
    environment: ENV,
    // No Session Replay integration: minors context (§5).
    integrations: [],
    sendDefaultPii: false,
    tracesSampleRate: 0.1,
    beforeSend: (event) => {
      // `Event` carries classes and functions, so it is not an EventValue — but what we walk
      // is JSON and `scrub` leaves keys alone: the two casts mark that boundary.
      const scrubbed = scrub(event as unknown as EventValue)
      return scrubbed as unknown as typeof event
    },
    beforeBreadcrumb: (breadcrumb) => {
      if (typeof breadcrumb.data?.url === 'string') {
        breadcrumb.data.url = scrubUrl(breadcrumb.data.url)
      }
      return breadcrumb
    },
  })
  return true
}
